import { z } from "zod";

import { entitlementsSourceSchema, organizationQueryKey } from "../../organizations/rules/queries";
import { organizationStatusSchema } from "../../organizations/rules/roles";
import { staffFor } from "../../organizations/rules/sdk";
import type { RestClientConfig } from "../../rest/rules/sdk";
import type { components } from "../../schema/api";

/**
 * StaffStatus is whether the signed-in user is a Mountaya staff member, and it is the console's
 * type: the published contract carries no staff vocabulary.
 */
export type StaffStatus = components["schemas"]["StaffStatus"];

/**
 * staffStatusSchema parses the answer of the staff status read: a single flag, decided by the
 * API on every request from a verified address on its staff allowlist.
 */
export const staffStatusSchema = z.object({ staff: z.boolean() }) satisfies z.ZodType<StaffStatus>;

/**
 * StaffOrganization is one organization as the staff search lists it, and it is the console's
 * type.
 */
export type StaffOrganization = components["schemas"]["StaffOrganization"];

/**
 * staffOrganizationSchema parses one row of the staff search: enough to recognize an
 * organization and to open it. `created_at` is a `Date` because the transport revives the wire
 * string before the parse.
 */
export const staffOrganizationSchema = z.object({
  awaiting_agreement: z.boolean(),
  created_at: z.date(),
  entitlements_source: entitlementsSourceSchema,
  id: z.string(),
  name: z.string(),
  status: organizationStatusSchema,
}) satisfies z.ZodType<StaffOrganization>;

/**
 * StaffOrganizationBilling is how a settled organization is billed, as the staff search narrows
 * by it: by agreement, or self-serve. It is the contract's type.
 */
export type StaffOrganizationBilling = components["schemas"]["StaffOrganizationBilling"];

/**
 * StaffOrganizationSearch is what a staff search narrows by: a term (an organization id, or a
 * part of a name), whether to list only the organizations awaiting their agreement, how the
 * organizations are billed, and the page of the listing.
 */
export interface StaffOrganizationSearch {
  /** Only the organizations whose creator asked for an agreement not set yet, when true. */
  readonly awaitingAgreement?: boolean | undefined;
  /**
   * Only the organizations billed this way: by agreement, or self-serve, which leaves out the
   * organizations awaiting their agreement. Omitted lists both.
   */
  readonly billing?: StaffOrganizationBilling | undefined;
  /**
   * Where the page starts: the `nextCursor` the previous page answered, unchanged. Omitted, the
   * listing starts at its first organization.
   */
  readonly cursor?: string | undefined;
  /** How many organizations the page holds, 1 to 100. The API defaults it to 50. */
  readonly limit?: number | undefined;
  /**
   * An organization id, or a part of a name, sent trimmed as the `q` query parameter. Blank or
   * omitted lists every organization.
   */
  readonly search?: string | undefined;
}

/**
 * StaffOrganizationPage is one page of the staff search: the organizations it holds, and the
 * cursor of the next page, so a reader tells whether another page exists.
 */
export interface StaffOrganizationPage {
  /** The cursor that asks for the next page, or null on the last one. */
  readonly nextCursor: string | null;
  /** The organizations of the page, ordered by name, then by id. */
  readonly organizations: StaffOrganization[];
}

/**
 * OrganizationEntitlementsRecord is an organization's entitlement record as staff read and
 * write it: the override staff stored, the entitlements in force, the source the record is
 * stamped with and the last change a staff member made. It is the console's type.
 */
export type OrganizationEntitlementsRecord =
  components["schemas"]["OrganizationEntitlementsRecord"];

/**
 * staffStatusQueryKey is the TanStack Query key of the staff status read. Flat under "staff":
 * the answer is about the signed-in user, and a sign-out clears the whole cache.
 */
export const staffStatusQueryKey = ["staff", "status"] as const;

/**
 * staffOrganizationsQueryKey is the TanStack Query key of one staff search: the prefix every
 * search shares, then what it narrows by, so a staff write invalidates every listing at once.
 */
export const staffOrganizationsQueryKey = (filters: StaffOrganizationSearch = {}) =>
  [
    "staff",
    "organizations",
    {
      awaitingAgreement: filters.awaitingAgreement ?? false,
      billing: filters.billing ?? null,
      cursor: filters.cursor ?? null,
      limit: filters.limit ?? null,
      search: filters.search?.trim() ?? "",
    },
  ] as const;

/**
 * organizationEntitlementsRecordQueryKey is the TanStack Query key of an organization's
 * entitlement record, under the organization's own root so an invalidation of the
 * organization reaches it.
 */
export const organizationEntitlementsRecordQueryKey = (organizationId: string) =>
  [...organizationQueryKey(organizationId), "entitlements", "record"] as const;

/** The envelope of the staff status read. */
const staffStatusResponseSchema = z.object({ data: staffStatusSchema });

/** The envelope of a staff search page, with the cursor of the next one. */
const staffOrganizationsResponseSchema = z.object({
  data: z.array(staffOrganizationSchema),
  extensions: z.object({
    metadata: z.object({ pagination: z.object({ next_cursor: z.string().nullable() }) }),
  }),
});

/**
 * fetchStaffStatus reads whether the signed-in user is a Mountaya staff member. Any signed-in
 * console session gets an answer: false for everybody but staff, never a refusal.
 */
export const fetchStaffStatus = async (config: RestClientConfig): Promise<StaffStatus> =>
  staffStatusResponseSchema.parse(await staffFor(config).getStaffStatus()).data;

/**
 * fetchStaffOrganizations reads one page of the staff search across every customer
 * organization. A caller who is not staff is refused with the transport's typed 403.
 */
export const fetchStaffOrganizations = async (
  config: RestClientConfig,
  filters: StaffOrganizationSearch = {},
): Promise<StaffOrganizationPage> => {
  const search = filters.search?.trim();
  const envelope = await staffFor(config).getStaffOrganizations({
    query: {
      awaiting_agreement: filters.awaitingAgreement === true ? true : undefined,
      billing: filters.billing,
      cursor: filters.cursor,
      limit: filters.limit,
      q: search === undefined || search === "" ? undefined : search,
    },
  });
  const parsed = staffOrganizationsResponseSchema.parse(envelope);

  return {
    nextCursor: parsed.extensions.metadata.pagination.next_cursor,
    organizations: parsed.data,
  };
};

/**
 * fetchOrganizationEntitlementsRecord reads an organization's entitlement record, the
 * document a staff edit starts from. A caller who is not staff is refused with a 403.
 */
export const fetchOrganizationEntitlementsRecord = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationEntitlementsRecord> => {
  const envelope = await staffFor(config).getOrganizationEntitlements({
    organization_id: organizationId,
  });

  return envelope.data;
};

/** staffStatusQueryOptions bundles the staff status key and its fetch. */
export const staffStatusQueryOptions = (config: RestClientConfig) => ({
  queryFn: () => fetchStaffStatus(config),
  queryKey: staffStatusQueryKey,
});

/**
 * FIRST_PAGE is where a staff search's walk starts: no cursor, so the listing's first page.
 */
const FIRST_PAGE: string | null = null;

/**
 * staffOrganizationsQueryOptions bundles one staff search's key and its walk: the first page,
 * then each page the reader asks for next, at the cursor the page before it answered and with
 * the same filters, until a page answers no cursor (ADR 0123: the end of a listing is told by
 * `next_cursor` alone). The key is the search's own without a cursor, so every page of one
 * search is one cache entry, and a staff write still invalidates every search by its prefix.
 */
export const staffOrganizationsQueryOptions = (
  config: RestClientConfig,
  filters: StaffOrganizationSearch = {},
) => ({
  getNextPageParam: (page: StaffOrganizationPage): string | undefined =>
    page.nextCursor ?? undefined,
  initialPageParam: FIRST_PAGE,
  queryFn: ({ pageParam }: { readonly pageParam: string | null }) =>
    fetchStaffOrganizations(config, { ...filters, cursor: pageParam ?? undefined }),
  queryKey: staffOrganizationsQueryKey({ ...filters, cursor: undefined }),
});

/** organizationEntitlementsRecordQueryOptions bundles the entitlement record's key and fetch. */
export const organizationEntitlementsRecordQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
) => ({
  queryFn: () => fetchOrganizationEntitlementsRecord(config, organizationId),
  queryKey: organizationEntitlementsRecordQueryKey(organizationId),
});
